import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import { Observable, catchError, exhaustMap, from, switchMap, tap } from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { LocalStorage } from 'src/app/shared/providers/local-storage/local-storage';
import { NetworkProvider } from 'src/app/shared/providers/network/network';
import { CrtData, ProfondeurHistorique } from '../../models/data';
import { ProfondeurTemporelle } from '../../models/profondeur-temporelle';
import { CrtHttpErrorHandlerService } from '../../providers/http-error-handler.service';
import { servicesUrl } from '../../config';

/**
 * Service Donnée Historique CRT
 */
@Injectable({
  providedIn: 'root',
})
export class CrtHistoriquesService extends LocalStorage {
  /**
   * Clef de stockage par site -> uniquement pour la date de récuperation
   * @type {string}
   */
  private readonly HISTORIQUES_STORAGE_KEY = 'crt-${siteId}-historiques';
  /**
   * clef de stockage du niveau de profondeur actuel des historiques pour un site
   * @type {ProfondeurHistorique}
   */
  private readonly PROFONDEUR_HISTO_KEY = 'crt-${idSite}-profondeur-histo';
  /**
   * Clef de stockage par donnée
   */
  private readonly HISTORIQUES_STORAGE_KEY_DATA = 'crt-${idSite}-historics-${dataId}';

  /**
   * Constructeur
   * @param {Storage} storage
   * @param {NetworkProvider} network
   * @param {HttpClient} http
   * @param crtHttpErrorHandler
   */
  constructor(
    protected override storage: Storage,
    protected override network: NetworkProvider,
    private http: HttpClient,
    private crtHttpErrorHandler: CrtHttpErrorHandlerService
  ) {
    super();
    this.serverRequestPeriod = 10;
  }

  /**
   * Récupère les historiques d'un site pour un nombre de jour donné
   * @param {string} idSite
   * @param {number} nbJours
   * @param {boolean} forceRefresh
   * @returns {Observable<CrtData[]>}
   */
  public getSiteHistoriques(idSite: number, nbJours: number, forceRefresh = false): Observable<CrtData[]> {
    const url = servicesUrl.getSiteHistorique({ idSite: idSite, nbJours: nbJours });
    const key = this.formatStorageKey(this.HISTORIQUES_STORAGE_KEY, { idSite });

    return this.shouldUseWebservice(key, forceRefresh).pipe(
      exhaustMap((shouldUseWebservice) => {
        if (shouldUseWebservice) {
          return this.http.get<CrtData[]>(url).pipe(
            tap(async (histoList) => {
              for (let histoData of histoList) {
                let keyData = this.formatStorageKey(this.HISTORIQUES_STORAGE_KEY_DATA, {
                  idSite: idSite,
                  dataId: histoData.id,
                });
                await this.storage.set(keyData, histoData);
                await this.setLastRequestDateToNow(keyData);
              }
              await this.setLastRequestDateToNow(key);
            }),
            catchError(async (e) => {
              return from(this.crtHttpErrorHandler.handleError(e)).pipe(
                switchMap(() => fromPromise(this.storage.get(key)))
              );
            })
          );
        } else {
          return fromPromise(this.storage.get(key));
        }
      })
    );
  }

  /**
   * Retourne la clé unique definissant l'axe de l'historique
   * @param data CrtData
   */
  public getUniqueAxisKey(data: CrtData): string {
    return data.associationLibelle
      ? data.associationLibelle.libelles.map((l) => l.valeur + l.valeurMinimun + l.valeurMaximun + l.libelle).join('')
      : data.unite;
  }

  /**
   * Retourne si le jeu de donnée à moins de 3 unités différentes
   * @param datas tableau de CrtData
   */
  public hasLessThanThreeDistinctUnits(datas: CrtData[]): boolean {
    const distinctUnits = datas.reduce((units, value) => {
      const unitKey = this.getUniqueAxisKey(value);
      if (units.findIndex((u) => u === unitKey) === -1) {
        units.push(unitKey);
      }
      return units;
    }, []);

    return distinctUnits.length < 3;
  }

  /**
   * Stocke la profondeur d'historique du site dans le stockage local
   */
  public setProfondeurHisto(idSite: number, profondeur: ProfondeurTemporelle) {
    let profKeyData = this.formatStorageKey(this.PROFONDEUR_HISTO_KEY, {
      idSite: idSite,
    });
    this.storage.set(profKeyData, profondeur);
  }

  /**
   * Récupère la profondeur d'historique du site du stockage local
   */
  public getProfondeurHistoIdSite(idSite: number): Observable<number> {
    const key = this.formatStorageKey(this.PROFONDEUR_HISTO_KEY, {
      idSite,
    });
    return fromPromise(this.storage.get(key));
  }

  /**
   * Reset la profondeur d'historique du site à 1 mois uniquement en mode connecté
   */
  public resetProfondeurHistoIdSite(idSite: number) {
    if (this.network.isConnected()) {
      const key = this.formatStorageKey(this.PROFONDEUR_HISTO_KEY, {
        idSite,
      });
      this.storage.set(key, ProfondeurHistorique.MOIS);
    }
  }

  /**
   * Supprime du stockage local la profondeur d'historique du site
   */
  public removeProfondeurHistoIdSite(idSite: number) {
    const key = this.formatStorageKey(this.PROFONDEUR_HISTO_KEY, {
      idSite,
    });
    this.storage.remove(key);
  }
}
